<script setup lang="ts">
import {computed, ref, shallowRef, watch} from "vue";
import {CalendarDate, DateValue, parseDate} from "@internationalized/date";
import type {DateRange} from "reka-ui";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamAbsence} from "@/core/interfaces/TeamPlanning";
import {AbsenceStoreRequest} from "@/core/http/resources/TeamPlanningResource";
import {CHOOSABLE_MOTIFS, OTHER_MOTIF, formatAbsenceRange, motifOf} from "@/planning/lib/absences";
import {staffMembers} from "@/team/planning";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/core/components/ui/select";
import {RangeCalendar} from "@/core/components/ui/range-calendar";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";

const {members, absence = null, saving = false} = defineProps<{
    /** The pharmacy team the absence can be recorded for. */
    members: LegacyAccess[];
    /** The absence being edited; null for a creation. */
    absence?: TeamAbsence | null;
    saving?: boolean;
}>();

const open = defineModel<boolean>('open', {required: true});

const emits = defineEmits<{
    (event: 'submit', payload: AbsenceStoreRequest): void;
}>();

const staff = computed<LegacyAccess[]>(() => staffMembers(members));

const currentMonth = (): CalendarDate => {
    const now = new Date();

    return new CalendarDate(now.getFullYear(), now.getMonth() + 1, 1);
};

/** Month the calendar shows. Two-way bound: the calendar writes it back. */
const placeholder = shallowRef<DateValue>(currentMonth());

const memberId = ref<number | null>(null);
const motif = ref<string>('');
const freeReason = ref<string>('');
// shallowRef, not ref: unwrapping recursively strips the private fields of the
// @internationalized/date classes and the calendar's own type stops matching.
const range = shallowRef<DateRange>({start: undefined, end: undefined});
const submitted = ref<boolean>(false);

/**
 * `saving` is a prop: the parent sets it inside its handler, but the
 * re-render that disables the button lands a tick later. Two quick clicks
 * would both get through and save the absence twice.
 */
const submitting = ref<boolean>(false);

const selectedMember = computed<LegacyAccess | undefined>(() =>
    staff.value.find(member => member.id === memberId.value));

const isOther = computed<boolean>(() => motif.value === OTHER_MOTIF.label);

/** The motive as it will be stored: the chip's label, or the typed text. */
const reason = computed<string>(() => isOther.value ? freeReason.value.trim() : motif.value);

const rangeComplete = computed<boolean>(() => Boolean(range.value.start && range.value.end));

const invalid = computed<boolean>(() => !memberId.value || !reason.value || !rangeComplete.value);

/**
 * An absence saved before the catalog existed, or under a free motive, has no
 * matching chip: it opens on "Autre" with its text kept editable.
 *
 * The shown month is reset along with the rest: it is two-way bound, so
 * browsing the calendar writes to it, and the component outlives a close —
 * an edition would otherwise open away from the period it has selected.
 */
watch([open, () => absence], () => {
    if (!open.value) {
        return;
    }

    submitted.value = false;
    submitting.value = false;

    if (!absence) {
        memberId.value = null;
        motif.value = '';
        freeReason.value = '';
        range.value = {start: undefined, end: undefined};
        placeholder.value = currentMonth();

        return;
    }

    const stored = motifOf(absence.reason);
    const starts = parseDate(absence.starts_at);

    memberId.value = absence.user_access_point_id;
    motif.value = stored.label;
    freeReason.value = stored === OTHER_MOTIF ? absence.reason : '';
    range.value = {start: starts, end: parseDate(absence.ends_at)};
    placeholder.value = starts;
});

watch(() => saving, (isSaving, wasSaving) => {
    if (wasSaving && !isSaving) {
        submitting.value = false;
    }
});

const chooseMotif = (label: string) => {
    motif.value = label;

    if (label !== OTHER_MOTIF.label) {
        freeReason.value = '';
    }
};

/** Y-m-d, the format both the server and `parseDate` speak. */
const toIsoDate = (date: DateValue): string => date.toString();

const preview = computed<string | null>(() => rangeComplete.value
    ? formatAbsenceRange({
        starts_at: toIsoDate(range.value.start as DateValue),
        ends_at: toIsoDate(range.value.end as DateValue)
    } as TeamAbsence)
    : null);

const handleSubmit = () => {
    submitted.value = true;

    if (invalid.value || submitting.value || saving) {
        return;
    }

    submitting.value = true;

    emits('submit', {
        user_access_point_id: memberId.value as number,
        starts_at: toIsoDate(range.value.start as DateValue),
        ends_at: toIsoDate(range.value.end as DateValue),
        reason: reason.value
    });
};

const MOTIFS = [...CHOOSABLE_MOTIFS, OTHER_MOTIF];
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="flex max-h-[90vh] max-w-md flex-col">
      <DialogHeader>
        <DialogTitle>
          {{ absence ? 'Modifier l\'absence' : 'Ajouter une absence' }}
        </DialogTitle>
        <DialogDescription>
          Elle masquera le planning du collaborateur sur toute la période.
        </DialogDescription>
      </DialogHeader>

      <form class="flex min-h-0 flex-1 flex-col" @submit.prevent="handleSubmit">
        <!-- A short window must not swallow the title or the save button:
             the fields scroll, the rest stays put. -->
        <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
          <div class="flex flex-col gap-1.5">
            <Label>Collaborateur</Label>
            <!-- The member is immutable server-side once the absence exists. -->
            <Select
              :model-value="memberId ? String(memberId) : undefined"
              :disabled="Boolean(absence)"
              @update:model-value="value => memberId = value ? Number(value) : null"
            >
              <SelectTrigger>
                <span v-if="selectedMember">{{ selectedMember.user?.name }}</span>
                <SelectValue v-else placeholder="Choisir un collaborateur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="member in staff"
                  :key="member.id"
                  :value="String(member.id)"
                >
                  {{ member.user?.name }}
                </SelectItem>
              </SelectContent>
            </Select>
            <p v-if="submitted && !memberId" class="text-xs text-red-light">
              Choisissez le collaborateur concerné.
            </p>
          </div>

          <div class="flex flex-col gap-1.5">
            <Label>Motif</Label>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="option in MOTIFS"
                :key="option.label"
                type="button"
                class="h-7 rounded-full border border-solid px-2.5 text-[11px] font-semibold transition-colors"
                :class="motif === option.label
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'"
                @click="chooseMotif(option.label)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div v-if="isOther" class="flex flex-col gap-1.5">
            <Label for="absence-reason">Raison</Label>
            <Input
              id="absence-reason"
              v-model="freeReason"
              maxlength="255"
              placeholder="Précisez la raison de l'absence"
            />
          </div>

          <p v-if="submitted && !reason" class="text-xs text-red-light">
            {{ isOther ? 'Précisez la raison de l\'absence.' : 'Choisissez un motif.' }}
          </p>

          <div class="flex flex-col gap-1.5">
            <Label>Période</Label>
            <!-- Centered rather than stretched: the grid's cells are a fixed
                 size, so a full-width table would pack them left and leave the
                 slack after the last column. -->
            <div class="flex justify-center rounded-lg border border-gray-200">
              <RangeCalendar
                v-model="range"
                v-model:placeholder="placeholder"
                locale="fr-FR"
              />
            </div>
            <p v-if="preview" class="text-xs font-semibold text-gray-600">
              {{ preview }}
            </p>
            <p v-else-if="submitted" class="text-xs text-red-light">
              Sélectionnez la période sur le calendrier.
            </p>
            <p v-else class="text-xs text-gray-400">
              Cliquez le premier jour, puis le dernier.
            </p>
          </div>
        </div>

        <DialogFooter class="pt-4">
          <Button type="submit" class="bg-primary hover:bg-primary-dark" :disabled="saving">
            {{ saving ? 'Enregistrement…' : 'Enregistrer l\'absence' }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
